import { HStack, IconButton, Text } from '@cad-arc';
import { QueryableEntityType } from 'mark43-federated-search';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { FormInputIconButton } from '../../../../forms/integrated-form-components/form-button';
import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EventPanelFormType, EventPanelMetadata } from '@mark43/cad-api';
import { useEventMetadataStore } from '../../event-metadata';
import {
    EventWorkspacePanelDexFormPrefill,
    EventWorkspacePanelDexNextFormPrefill,
} from '../../../../panels/workspace-panel-types';
import { getUniversalSearchFormLabel } from '../../../../common/helpers/get-usx-labels';
import { useHandleOpenInDexButtonClick } from '../../../../panels/hooks/use-handle-open-in-dex-button-click';
import { useIsDexFullyOperational } from '../../../../federated-search/federated-search-remote-state-context';
import {
    getFirearmDexNextPrefillValues,
    getOtherItemDexNextPrefillValues,
} from './property-helper';

export function EventPanelFormPropertyCardControls({
    remove,
    allowMembersControl,
    eventForm,
    baseKey,
    index,
    propertyName,
    isNewEvent,
    isPersisted,
}: {
    allowMembersControl?: boolean;
    dexEnabled: boolean;
    eventForm: UseFormReturn<EventPanelFormType>;
    remove: UseTrackedFieldArrayRemove;
    baseKey:
        | 'firearms'
        | 'mergedFirearms'
        | 'drugs'
        | 'mergedDrugs'
        | 'otherItems'
        | 'mergedOtherItems';
    index: number;
    propertyName: string;
    isPersisted: boolean;
    isNewEvent: boolean;
}) {
    const { dexNextEnabled } = useEnabledFeatures();
    const isDexFullyOperational = useIsDexFullyOperational();
    const { agencyEventNumber: eventNumber, eventId } =
        useEventMetadataStore().state?.metadata ?? ({} as EventPanelMetadata);
    const canDexQuery =
        baseKey === 'firearms' ||
        baseKey === 'mergedFirearms' ||
        baseKey === 'otherItems' ||
        baseKey === 'mergedOtherItems';

    const isFirearm = baseKey === 'firearms' || baseKey === 'mergedFirearms';
    const isOtherItem = baseKey === 'otherItems' || baseKey === 'mergedOtherItems';
    const isDrugs = baseKey === 'drugs' || baseKey === 'mergedDrugs';

    const dexNextEntityType = isFirearm
        ? QueryableEntityType.FIREARM
        : isOtherItem
        ? QueryableEntityType.ARTICLE
        : undefined;

    const handleOpenInDexButtonClick = useHandleOpenInDexButtonClick({
        dexFormQuery: isFirearm ? 'FIREARM_MASTER_QUERY' : 'ARTICLE_MASTER_QUERY',
        dexNextEntityType,
        getDexFormValues,
        getDexNextFormValues,
    });

    const propertyId = useWatch({
        control: eventForm.control,
        name: `${baseKey}.${index}.id`,
    });

    function onDelete() {
        eventForm.setValue(`${baseKey}.${index}.isRemoved`, true);
        remove(propertyId, index);
    }

    function getDexFormValues(): EventWorkspacePanelDexFormPrefill {
        if (isDrugs) return {};
        const [serialNumber] = eventForm.getValues([`${baseKey}.${index}.serialNumber`]);

        // For Firearms Query
        if (isFirearm) {
            return {
                eventNumber,
                firearm: {
                    gunSerialNumber: serialNumber ?? undefined,
                },
            };
        }
        // For Other Items Query
        if (isOtherItem) {
            return {
                eventNumber,
                article: {
                    articleSerialNumber: serialNumber ?? undefined,
                },
            };
        }
        return {};
    }

    function getDexNextFormValues(): EventWorkspacePanelDexNextFormPrefill {
        if (isDrugs) return {};

        let prefillValues: NonNullable<EventWorkspacePanelDexNextFormPrefill['prefillValues']> = {};

        if (isFirearm) {
            const [
                serialNumber,
                firearmMake,
                caliber,
                description,
                firearmType,
            ] = eventForm.getValues([
                `${baseKey}.${index}.serialNumber`,
                `${baseKey}.${index}.firearmMake`,
                `${baseKey}.${index}.caliber`,
                `${baseKey}.${index}.description`,
                `${baseKey}.${index}.firearmType`,
            ]);

            prefillValues = getFirearmDexNextPrefillValues({
                serialNumber,
                firearmMake: firearmMake?.value ? String(firearmMake?.value) : undefined,
                caliber: caliber?.value ? String(caliber?.value) : undefined,
                gunType: firearmType?.value ? String(firearmType?.value) : undefined,
                freeText: description,
            });
        }

        if (isOtherItem) {
            const [serialNumber, itemCategory, description, model] = eventForm.getValues([
                `${baseKey}.${index}.serialNumber`,
                `${baseKey}.${index}.itemCategory`,
                `${baseKey}.${index}.description`,
                `${baseKey}.${index}.model`,
            ]);

            prefillValues = getOtherItemDexNextPrefillValues({
                serialNumber,
                articleModel: model,
                articleCategory: itemCategory?.value ? String(itemCategory?.value) : undefined,
                freeText: description,
            });
        }

        prefillValues.CAD_EVENT_SELECT_VALUE = eventId ? String(eventId) : undefined;

        return {
            eventNumber,
            entityType: dexNextEntityType,
            prefillValues,
        };
    }

    return (
        <HStack
            padding={2}
            pl={1}
            spacing={2}
            width="100%"
            alignItems="center"
            justifyContent="space-between"
        >
            <Text variant="headingSm">{`${propertyName} ${index + 1}`}</Text>
            <HStack>
                {!isNewEvent && isPersisted && canDexQuery && isDexFullyOperational && (
                    <IconButton
                        size="sm"
                        style={{ alignSelf: 'flex-start' }}
                        icon="DataExchangeInquiries"
                        variant="ghost"
                        aria-label={`Open ${getUniversalSearchFormLabel(dexNextEnabled)}`}
                        onClick={handleOpenInDexButtonClick}
                        data-testid="OPEN_PROPERTY_IN_DEX"
                        tabIndex={-1}
                    />
                )}
                {allowMembersControl ? (
                    <FormInputIconButton
                        style={{ alignSelf: 'flex-start' }}
                        size="sm"
                        aria-label="Delete"
                        variant="ghost"
                        icon="TrashCan"
                        onClick={onDelete}
                        data-testid={`${propertyName.toLowerCase()}-remove-${propertyId}`}
                        tabIndex={-1}
                    />
                ) : undefined}
            </HStack>
        </HStack>
    );
}
